import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SearchBar from './SearchBar';
import LanguageSwitcher from '../common/LanguageSwitcher';
import Logo from '../common/Logo';
import { useAuthStore } from '../../stores/authStore';

export default function Header() {
  const { t } = useTranslation();
  const { user, isAuthenticated, logout } = useAuthStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    await logout();
    setIsUserMenuOpen(false);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      {/* Main header */}
      <div className="bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-6">
            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 text-gray-700 hover:text-primary transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <div className="group-hover:scale-105 transition-transform">
                <Logo size="xl" className="hidden sm:block" />
                <Logo size="lg" className="sm:hidden" />
              </div>
            </Link>

            {/* Search bar - desktop */}
            <div className="hidden md:block flex-1 max-w-2xl">
              <SearchBar />
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-4">
              {/* Language Switcher */}
              <div className="hidden sm:block">
                <LanguageSwitcher />
              </div>

              {/* Wishlist */}
              <Link
                to="/wishlist"
                className="relative p-2 text-gray-600 hover:text-primary transition-colors"
                title={t('nav.favorites')}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </Link>

              {/* User menu */}
              {isAuthenticated && user ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 p-2 text-gray-600 hover:text-primary transition-colors"
                  >
                    <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold text-sm">
                      {user.firstName?.[0] || user.email[0].toUpperCase()}
                    </div>
                    <span className="hidden lg:inline text-sm text-gray-700">
                      {user.firstName || user.email.split('@')[0]}
                    </span>
                    <svg className={`w-4 h-4 text-gray-400 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* User dropdown */}
                  {isUserMenuOpen && (
                    <div className="absolute end-0 mt-2 w-56 bg-white rounded-xl shadow-dropdown py-2 z-50 animate-fade-in border border-gray-100">
                      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                        <p className="text-sm font-semibold text-gray-800">
                          {user.firstName || t('auth.welcome')}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>
                      <Link
                        to="/wishlist"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-primary/5 hover:text-primary transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                        {t('nav.favorites')}
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        {t('auth.logout')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="hidden sm:flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-medium text-sm hover:bg-primary-dark transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>{t('auth.loginRegister')}</span>
                </Link>
              )}
            </div>
          </div>

          {/* Search bar - mobile */}
          <div className="md:hidden mt-4">
            <SearchBar />
          </div>
        </div>

        {/* Navigation bar */}
        <nav className="hidden md:block border-t border-gray-100">
          <div className="container mx-auto px-4">
            <ul className="flex items-center gap-1">
              <li>
                <Link
                  to="/"
                  className="flex items-center gap-2 px-4 py-2.5 text-gray-700 hover:text-primary transition-colors text-sm font-medium"
                >
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link
                  to="/products?hotProduct=true"
                  className="flex items-center gap-1.5 px-4 py-2.5 text-gray-700 hover:text-primary transition-colors text-sm font-medium"
                >
                  <span>🔥</span>
                  {t('categories.hotProducts', 'Hot Products')}
                </Link>
              </li>
              <li>
                <Link
                  to="/products?dailyDeal=true"
                  className="flex items-center gap-1.5 px-4 py-2.5 text-gray-700 hover:text-primary transition-colors text-sm font-medium"
                >
                  <span>⚡</span>
                  {t('categories.dailyDeal', 'Daily Deals')}
                </Link>
              </li>
              <li>
                <Link
                  to="/wishlist"
                  className="flex items-center gap-1.5 px-4 py-2.5 text-gray-700 hover:text-primary transition-colors text-sm font-medium"
                >
                  {t('nav.favorites')}
                </Link>
              </li>
              <li className="ms-auto">
                <Link
                  to="/contact"
                  className="flex items-center gap-1.5 px-4 py-2.5 text-gray-700 hover:text-primary transition-colors text-sm font-medium"
                >
                  {t('nav.contact')}
                </Link>
              </li>
            </ul>
          </div>
        </nav>
      </div>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div
            className="mobile-menu-drawer"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile menu header */}
            <div className="bg-gradient-to-r from-[#0F2A44] to-[#1a3a5c] p-5 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Logo size="md" variant="icon" />
                  <span className="font-bold text-lg">Smart Shopping IL</span>
                </div>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* User info in mobile menu */}
              {isAuthenticated && user ? (
                <div className="flex items-center gap-3 pt-2 border-t border-white/20">
                  <div className="w-10 h-10 bg-white text-primary rounded-full flex items-center justify-center font-bold">
                    {user.firstName?.[0] || user.email[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium">{user.firstName || user.email.split('@')[0]}</p>
                    <p className="text-xs text-white/70">{user.email}</p>
                  </div>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center gap-2 pt-2 border-t border-white/20"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>{t('auth.loginRegister')}</span>
                </Link>
              )}
            </div>

            {/* Mobile menu navigation */}
            <nav className="py-2">
              <Link
                to="/"
                className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span className="font-medium text-gray-800">{t('nav.home')}</span>
              </Link>

              <Link
                to="/products?hotProduct=true"
                className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="text-2xl">🔥</span>
                <span className="font-medium text-gray-800">{t('categories.hotProducts', 'Hot Products')}</span>
              </Link>

              <Link
                to="/products?dailyDeal=true"
                className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="text-2xl">⚡</span>
                <span className="font-medium text-gray-800">{t('categories.dailyDeal', 'Daily Deals')}</span>
              </Link>

              <Link
                to="/wishlist"
                className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <span className="font-medium text-gray-800">{t('nav.favorites')}</span>
              </Link>

              <div className="border-t border-gray-100 my-2"></div>

              <Link
                to="/terms"
                className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="font-medium text-gray-800">{t('nav.terms')}</span>
              </Link>

              <Link
                to="/contact"
                className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="font-medium text-gray-800">{t('nav.contact')}</span>
              </Link>

              <div className="border-t border-gray-100 my-2"></div>

              {/* Language Switcher - Mobile */}
              <div className="px-5 py-3">
                <LanguageSwitcher variant="mobile" />
              </div>

              {isAuthenticated && (
                <>
                  <div className="border-t border-gray-100 my-2"></div>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-red-50 transition-colors text-red-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span className="font-medium">{t('auth.logout')}</span>
                  </button>
                </>
              )}
            </nav>

          </div>
        </div>
      )}
    </header>
  );
}
